<template>
  <div>
    <Sidebar class="app-left-container" />
    <div class="app-main-container" :class="{ collapsed: !isShowSider }">
      <Navbar />
      <AppMain />
    </div>
    <!-- 客服入口只留一个：配置了 Libredesk（右下角圆形挂件）的环境就不再挂自家的
         SupportChatbot，两个叠在右下角会重复。生产环境还没配 Libredesk，那里照旧用它。 -->
    <SupportChatbot v-if="!libredeskEnabled" />
    <!-- Sibling of Sidebar, not nested inside it: .sidebar-container is
         position:fixed with its own z-index, which traps any z-index'd
         descendant inside that stacking context no matter how high its own
         z-index is set — this overlay needs to sit above page content that
         lives entirely outside the sidebar (e.g. HotelReports' sticky nav
         bar), so it has to escape that stacking context by not being inside
         it at all. -->
    <SidebarGuide />
  </div>
</template>

<script>
import { Sidebar, AppMain, Navbar } from "./components";
import { mapState } from "vuex";
import SupportChatbot from "@/components/common/SupportChatbot.vue";
import SidebarGuide from "@/layout/components/Sidebar/SidebarGuide.vue";
import { createEntityReporter, currentEntity, entityKey } from "@/utils/libredeskEntity.js";
import { isLibredeskConfigured } from "@/utils/libredesk.js";

// 切店后等这么久再上报。侧边栏切换是"提交 + 整页刷新"，刷新后 created 会带着新实体再报一次；
// 刷新够快时这个延迟能省掉刷新前那一个（可能是中间态的）请求。刷新慢于它时那个请求仍会发出，
// 但刷新后的上报更晚到、会覆盖它，所以只是多一次请求，不会留下错的实体。
const ENTITY_REPORT_DELAY_MS = 300;
// 标签页重新可见时强制重报（别的标签页 / 设备可能已经改了服务端的值），最多这么频繁一次。
const ENTITY_REFRESH_MIN_INTERVAL_MS = 10000;

export default {
  name: "Layout",
  components: {
    Sidebar,
    AppMain,
    Navbar,
    SupportChatbot,
    SidebarGuide,
  },
  computed: {
    ...mapState({
      isShowSider: state => state.hotel.isShowSider,
    }),
    // 构建期环境变量，运行中不会变
    libredeskEnabled() {
      return isLibredeskConfigured();
    },
    goalEntityKey() {
      const {isHotel, currentHotelId} = this.$store.state.hotel;
      const {selectedRestaurantId} = this.$store.state.restaurant;
      if (isHotel) {
        return currentHotelId ? `hotel:${currentHotelId}` : null;
      }
      return selectedRestaurantId ? `restaurant:${selectedRestaurantId}` : null;
    },
    // 客服系统看到的"客户所在页面"。品牌模式优先，和侧边栏显示的一致。
    libredeskEntityKey() {
      return entityKey(currentEntity(this.$store.state));
    },
  },
  watch: {
    goalEntityKey(newKey, oldKey) {
      if (newKey && oldKey && newKey !== oldKey) {
        this.$store.dispatch("goal/update_goal");
      }
    },
    // 首次上报之后，实体再变（不刷新页面的切换，比如纯品牌账号在首次上报后才进入品牌模式）
    // 就重报一次。退出登录时 token 先清空，不报 —— 否则会带着空 token 打 /auth/me 触发 401。
    libredeskEntityKey() {
      if (!this.entityReporter || !this.entityReporter.started) return;
      clearTimeout(this.entityReportTimer);
      this.entityReportTimer = setTimeout(() => {
        if (!this.$store.state.auth.token) return;
        this.entityReporter.report(this.libredeskEntity());
      }, ENTITY_REPORT_DELAY_MS);
    },
  },
  methods: {
    // 当前实体；/auth/entities 没拉到时返回 undefined（= 不知道，不带参数，api 不动旧值），
    // 而不是 null（= 没有实体，api 会清空）。之后列表拉到了就恢复正常上报。
    libredeskEntity() {
      const state = this.$store.state;
      if (this.entityContextUnknown) {
        const loaded =
          (state.hotel.hotelList || []).length ||
          (state.restaurant.restaurantList || []).length ||
          (state.auth.brands || []).length;
        if (!loaded) return undefined;
        this.entityContextUnknown = false;
      }
      return currentEntity(state);
    },
    onVisibilityChange() {
      if (document.visibilityState !== "visible") return;
      if (!this.entityReporter || !this.entityReporter.started || !this.$store.state.auth.token) return;
      const now = Date.now();
      if (now - (this.entityRefreshedAt || 0) < ENTITY_REFRESH_MIN_INTERVAL_MS) return;
      this.entityRefreshedAt = now;
      this.entityReporter.report(this.libredeskEntity(), { force: true });
    },
  },
  beforeDestroy() {
    clearTimeout(this.entityReportTimer);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },
  async created() {
    // hotelList/restaurantList/brands are not persisted as source of truth — fetch fresh on
    // every app load (via the merged GET /auth/entities) so the sidebar selector can display
    // hotel/restaurant names. After the list arrives, re-validate the persisted currentHotelId:
    // keep it if still valid, fall back to the first hotel if the user lost access or the ID is stale.
    const savedId = this.$store.state.hotel.currentHotelId;
    // Resolves to the response on success and to undefined on failure (it notifies and swallows).
    const entities = await this.$store.dispatch("auth/fetchEntities");
    this.entityContextUnknown = !entities;
    this.$store.commit("hotel/SET_CURRENT_HOTEL", savedId);
    // Fetch restaurant list so the sidebar RestaurantBox can display names.
    // Brand-only accounts (no hotels, no restaurants) auto-enter brand mode —
    // see auth/detectBrandOnlyMode.
    this.$store.dispatch("auth/detectBrandOnlyMode").catch(() => {});
    // Populate auth email on every app load (including after page refresh), and tell
    // the live chat which hotel/restaurant/brand the user is on. Failure is non-fatal —
    // the email field stays empty and the chat keeps its previous context.
    // The first report also identifies the chat widget; later ones (see the
    // libredeskEntityKey watcher) only rewrite the context server-side.
    this.entityReporter = createEntityReporter((params, isFirst) =>
      this.$store.dispatch("auth/me", { params, identify: isFirst }),
    );
    this.entityReporter.report(this.libredeskEntity());
    this.entityRefreshedAt = Date.now();
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.$store.dispatch("goal/update_goal");
    // Authoritative category/subcategory list for the current module — fetched
    // once here (see store/modules/serviceCategories.js), not per-page.
    const entityType = this.$store.state.hotel.isHotel ? "Hotel" : "Restaurant";
    this.$store.dispatch("serviceCategories/fetchServiceCategories", entityType).catch(() => {});
  },
};
</script>

<style>
.app-main-container {
  position: relative;
  margin-left: 248px;
  transition: margin-left 0.22s ease;
}

.app-main-container.collapsed {
  margin-left: 82px;
}

/* <1024px：侧边栏默认完全隐藏、以浮层抽屉形式弹出（见 Sidebar/index.vue），不再挤占正文
   空间——同一个断点、同一份 .collapsed 特殊性写法，确保规则能覆盖上面两条。 */
@media (max-width: 1023px) {
  .app-main-container,
  .app-main-container.collapsed {
    margin-left: 0;
  }
}
</style>
